// VisionOverlay — composable visualization layers drawn on top of the video.
//
// Reads pose + mask data directly from refs exposed by useVisionPipeline,
// which means redraw rate is decoupled from React re-renders. The overlay
// runs its own requestAnimationFrame loop at 60fps regardless of the
// sidebar's 15 Hz update rate.
//
// Five independent layers, each toggled by the `modes` prop:
//   1. segmentation  — per-pixel body part coloring (head/torso/arms/legs)
//   2. contour       — glowing silhouette outline of the frontal area
//   3. skeleton      — pose landmarks + bone lines with depth shading
//   4. mesh          — dense subdivided 3D body mesh projected onto the
//                      video, shaded by face-normal-dot-camera
//   5. pose3d        — small rotating 3D pose inset in the corner

import React, { useEffect, useRef } from 'react';
import { VisualizationRefs } from '../hooks/useVisionPipeline';
import { LABEL } from '../vision/bodySegments';
import { colors } from '../theme';

export interface VisionOverlayModes {
  segmentation: boolean;
  contour: boolean;
  skeleton: boolean;
  mesh: boolean;
  pose3d: boolean;
  /** Track 2.2 — drag heatmap projected onto the silhouette. Colors each
   *  region by its per-pixel drag (watts per labeled pixel) so a small
   *  region carrying a lot of drag (arms!) reads as hot, while a large
   *  region carrying less reads as cool. Riders see what to fix. */
  dragHeatmap: boolean;
  /** When true, video feed is hidden and overlays render on black. */
  videoOff: boolean;
  /** Camera rotation in degrees (0, 90, 180, 270). Applied as CSS transform
   *  to the video + overlay container so MediaPipe still reads the raw frame. */
  rotation: number;
}

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  refs: VisualizationRefs;
  modes: VisionOverlayModes;
  /** Track 2.2 — per-region watts from SmoothedAnalysis.segments. When
   *  undefined the heatmap layer falls back to flat region colors. */
  regionWatts?: {
    head?: number;
    torso?: number;
    arms?: number;
    legs?: number;
  };
}

// Colors for per-pixel body part segmentation.
// Tuned to be vivid but leave the underlying video visible.
const SEGMENT_COLORS: Record<number, [number, number, number, number]> = {
  [LABEL.HEAD]: [0, 217, 255, 140], // cyan
  [LABEL.TORSO]: [106, 255, 69, 140], // brand green
  [LABEL.ARMS]: [255, 0, 255, 140], // magenta
  [LABEL.LEGS]: [255, 181, 71, 140], // amber
};

// MediaPipe Pose skeleton connections
const SKELETON_EDGES: [number, number][] = [
  [11, 12], // shoulders
  [11, 13],
  [13, 15], // left arm
  [12, 14],
  [14, 16], // right arm
  [11, 23],
  [12, 24], // torso sides
  [23, 24], // hips
  [23, 25],
  [25, 27], // left leg
  [24, 26],
  [26, 28], // right leg
  [0, 11],
  [0, 12], // neck
];

export default function VisionOverlay({ videoRef, refs, modes, regionWatts }: Props) {
  // Track 2.2 — keep latest watts in a ref so the RAF loop doesn't need a
  // re-render to pick up new values. The loop closure already reads refs
  // synchronously each frame.
  const wattsRef = useRef(regionWatts);
  wattsRef.current = regionWatts;
  // Main overlay canvas — sized to the video element, drawn each RAF frame.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Offscreen canvas for the segmentation layer (at mask resolution, scaled up).
  const segCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const segImageDataRef = useRef<ImageData | null>(null);
  const rafRef = useRef<number | null>(null);
  // Continuously advancing rotation for the 3D pose inset.
  const rotationRef = useRef(0);

  // Set the canvas's internal pixel resolution to match its displayed size.
  // CSS handles positioning (100% width/height via inset:0). We only need to
  // set the pixel buffer dimensions so drawing is crisp.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      // Use the canvas's own CSS-driven layout size as the source of truth.
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      canvas.width = w * window.devicePixelRatio;
      canvas.height = h * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => {
      window.removeEventListener('resize', resize);
      ro.disconnect();
    };
  }, []);

  // Main render loop — runs at the video's natural framerate (60fps).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);

      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const mask = refs.maskRef.current;
      const labelMap = refs.labelMapRef.current;
      const edgeMap = refs.edgeMapRef.current;
      const maskW = refs.maskWidthRef.current;
      const maskH = refs.maskHeightRef.current;
      const landmarks = refs.landmarksRef.current;
      const worldLandmarks = refs.worldLandmarksRef.current;

      if (!mask || !maskW || !maskH) return;

      // ---------- Layer 1: Segmentation OR drag heatmap (mutually exclusive) ----------
      // Track 2.2 — when the heatmap is active it replaces the flat
      // segmentation colors with per-region drag intensity. Riders typically
      // want one or the other, not both stacked.
      if (modes.dragHeatmap && labelMap) {
        drawDragHeatmapLayer(
          ctx, labelMap, maskW, maskH, w, h,
          segCanvasRef, segImageDataRef, wattsRef.current,
        );
      } else if (modes.segmentation && labelMap) {
        drawSegmentationLayer(ctx, labelMap, maskW, maskH, w, h, segCanvasRef, segImageDataRef);
      }

      // ---------- Layer 2: Contour ----------
      if (modes.contour && edgeMap) {
        drawContourLayer(ctx, edgeMap, maskW, maskH, w, h);
      }

      // Layer 3 (3D Mesh) is rendered by <MeshCanvas> in WebGL, not here.

      // ---------- Layer 4: Skeleton (drawn on top of mesh) ----------
      if (modes.skeleton && landmarks) {
        drawSkeletonLayer(ctx, landmarks, w, h);
      }

      // ---------- Layer 5: 3D pose inset ----------
      if (modes.pose3d && worldLandmarks) {
        rotationRef.current += 0.015;
        drawPose3dInset(ctx, worldLandmarks, w, h, rotationRef.current);
      }
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [refs, modes]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Layer renderers
// ---------------------------------------------------------------------------

function drawSegmentationLayer(
  ctx: CanvasRenderingContext2D,
  labelMap: Uint8Array,
  maskW: number,
  maskH: number,
  canvasW: number,
  canvasH: number,
  segCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>,
  segImageDataRef: React.MutableRefObject<ImageData | null>
) {
  // Draw into an offscreen canvas at mask resolution, then scale up with
  // canvas smoothing disabled so the pixel grid reads as "AI vision".
  let seg = segCanvasRef.current;
  if (!seg || seg.width !== maskW || seg.height !== maskH) {
    seg = document.createElement('canvas');
    seg.width = maskW;
    seg.height = maskH;
    segCanvasRef.current = seg;
    segImageDataRef.current = new ImageData(maskW, maskH);
  }
  const segCtx = seg.getContext('2d');
  if (!segCtx) return;

  const imageData = segImageDataRef.current!;
  const rgba = imageData.data;

  for (let i = 0; i < labelMap.length; i++) {
    const label = labelMap[i];
    const color = SEGMENT_COLORS[label];
    if (color) {
      const j = i * 4;
      rgba[j] = color[0];
      rgba[j + 1] = color[1];
      rgba[j + 2] = color[2];
      rgba[j + 3] = color[3];
    } else {
      rgba[i * 4 + 3] = 0; // transparent
    }
  }
  segCtx.putImageData(imageData, 0, 0);

  // Scale up to the main canvas with smoothing off for crisp pixels,
  // then re-enable smoothing for the other layers.
  const prev = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = true;
  ctx.globalAlpha = 0.75;
  ctx.drawImage(seg, 0, 0, canvasW, canvasH);
  ctx.globalAlpha = 1;
  ctx.imageSmoothingEnabled = prev;
}

function drawContourLayer(
  ctx: CanvasRenderingContext2D,
  edgeMap: Uint8Array,
  maskW: number,
  maskH: number,
  canvasW: number,
  canvasH: number
) {
  // Scale factor from mask pixel coords to canvas coords.
  const sx = canvasW / maskW;
  const sy = canvasH / maskH;

  // Draw edge pixels as a neon silhouette. Two-pass: wide soft glow then
  // a crisp 1px line on top.
  ctx.save();
  ctx.fillStyle = colors.brand;
  ctx.shadowColor = colors.brand;
  ctx.shadowBlur = 16;

  // Single pass over the edge map. Rectangles are cheap on GPU-backed
  // canvases and this avoids per-pixel path-building overhead.
  const size = Math.max(2, Math.min(sx, sy) * 1.2);
  for (let y = 0; y < maskH; y++) {
    const rowBase = y * maskW;
    for (let x = 0; x < maskW; x++) {
      if (edgeMap[rowBase + x]) {
        ctx.fillRect(x * sx, y * sy, size, size);
      }
    }
  }

  ctx.restore();
}

function drawSkeletonLayer(
  ctx: CanvasRenderingContext2D,
  landmarks: { x: number; y: number; z: number; visibility: number }[],
  w: number,
  h: number
) {
  // Bones — colored by depth (negative z = closer to camera = brighter).
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(3, w / 400);

  for (const [a, b] of SKELETON_EDGES) {
    const la = landmarks[a];
    const lb = landmarks[b];
    if ((la.visibility ?? 0) < 0.4 || (lb.visibility ?? 0) < 0.4) continue;

    const avgZ = (la.z + lb.z) / 2;
    // avgZ is typically in the range ~[-0.5, 0.5] in image-normalized space
    const depth = Math.max(0, Math.min(1, 0.5 - avgZ));
    const alpha = 0.55 + depth * 0.45;

    ctx.strokeStyle = `rgba(106, 255, 69, ${alpha})`;
    ctx.shadowColor = colors.brand;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(la.x * w, la.y * h);
    ctx.lineTo(lb.x * w, lb.y * h);
    ctx.stroke();
  }

  // Joint dots
  ctx.shadowBlur = 0;
  for (const lm of landmarks) {
    if ((lm.visibility ?? 0) < 0.4) continue;
    const depth = Math.max(0, Math.min(1, 0.5 - lm.z));
    const r = Math.max(4, w / 260) * (0.7 + depth * 0.6);
    ctx.fillStyle = `rgba(255, 255, 255, ${0.7 + depth * 0.3})`;
    ctx.beginPath();
    ctx.arc(lm.x * w, lm.y * h, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}


// ---------------------------------------------------------------------------
// 3D pose inset — small rotating viewport in the top-left showing the rider's
// pose reconstructed from worldLandmarks (real-world 3D in metres). Rotates
// continuously around the vertical axis so you see yourself from every angle.
// ---------------------------------------------------------------------------

const POSE3D_SIZE = 200;
const POSE3D_MARGIN = 20;
const POSE3D_TOP_OFFSET = 88; // below the floating toolbar

function drawPose3dInset(
  ctx: CanvasRenderingContext2D,
  worldLandmarks: { x: number; y: number; z: number; visibility: number }[],
  canvasW: number,
  canvasH: number,
  rotation: number
) {
  const dpr = window.devicePixelRatio;
  const size = POSE3D_SIZE * dpr;
  const margin = POSE3D_MARGIN * dpr;
  const px = margin;
  const py = POSE3D_TOP_OFFSET * dpr;

  ctx.save();
  // Panel background
  ctx.fillStyle = 'rgba(10, 14, 26, 0.75)';
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 1 * dpr;
  roundRect(ctx, px, py, size, size, 12 * dpr);
  ctx.fill();
  ctx.stroke();

  // Clip to panel
  ctx.beginPath();
  roundRect(ctx, px, py, size, size, 12 * dpr);
  ctx.clip();

  // Label
  ctx.fillStyle = colors.brand;
  ctx.font = `600 ${10 * dpr}px "JetBrains Mono", monospace`;
  ctx.textBaseline = 'top';
  ctx.fillText('3D POSE', px + 12 * dpr, py + 10 * dpr);

  // Compute centroid + bounds of visible landmarks
  let minY = Infinity, maxY = -Infinity;
  let cx = 0, cy = 0, cz = 0;
  let count = 0;
  for (const lm of worldLandmarks) {
    if ((lm.visibility ?? 0) < 0.3) continue;
    cx += lm.x;
    cy += lm.y;
    cz += lm.z;
    count++;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }
  if (count === 0) {
    ctx.restore();
    return;
  }
  cx /= count;
  cy /= count;
  cz /= count;

  const bodyHeight = Math.max(0.001, maxY - minY);
  const drawSize = size * 0.7;
  const scale = drawSize / bodyHeight;

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const project = (lm: { x: number; y: number; z: number }) => {
    const dx = lm.x - cx;
    const dz = lm.z - cz;
    const rx = dx * cos + dz * sin;
    const dy = lm.y - cy;
    return {
      x: px + size / 2 + rx * scale,
      y: py + size / 2 + dy * scale,
      depth: -dx * sin + dz * cos,
    };
  };

  // Bones with depth-fade
  ctx.lineCap = 'round';
  ctx.lineWidth = 2.5 * dpr;
  for (const [a, b] of SKELETON_EDGES) {
    const la = worldLandmarks[a];
    const lb = worldLandmarks[b];
    if ((la.visibility ?? 0) < 0.3 || (lb.visibility ?? 0) < 0.3) continue;
    const pa = project(la);
    const pb = project(lb);
    const avgDepth = (pa.depth + pb.depth) / 2;
    const depthNorm = Math.max(0, Math.min(1, 0.5 + avgDepth * 1.5));
    ctx.strokeStyle = `rgba(106, 255, 69, ${0.35 + depthNorm * 0.6})`;
    ctx.beginPath();
    ctx.moveTo(pa.x, pa.y);
    ctx.lineTo(pb.x, pb.y);
    ctx.stroke();
  }

  // Joints
  for (const lm of worldLandmarks) {
    if ((lm.visibility ?? 0) < 0.3) continue;
    const p = project(lm);
    const depthNorm = Math.max(0, Math.min(1, 0.5 + p.depth * 1.5));
    ctx.fillStyle = `rgba(255, 255, 255, ${0.5 + depthNorm * 0.5})`;
    ctx.beginPath();
    ctx.arc(p.x, p.y, (2.2 + depthNorm * 1.2) * dpr, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// Track 2.2 — Drag heatmap renderer
// ---------------------------------------------------------------------------
//
// Colors each labeled pixel by its region's drag intensity, computed as
// per-region watts ÷ pixel count. A region with a small silhouette but a
// lot of attributed drag (typically arms) reads very hot; a large region
// carrying less drag relatively (legs in TT position) reads cool.
//
// The intensity scale auto-fits to the rider's current frame — the highest-
// per-pixel-watt region in the frame is the hot endpoint, others scale
// proportionally. That way the heatmap stays vivid even when whole-body
// drag is high (rider sat up) or low (rider crouched).

function drawDragHeatmapLayer(
  ctx: CanvasRenderingContext2D,
  labelMap: Uint8Array,
  maskW: number,
  maskH: number,
  canvasW: number,
  canvasH: number,
  segCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>,
  segImageDataRef: React.MutableRefObject<ImageData | null>,
  watts: { head?: number; torso?: number; arms?: number; legs?: number } | undefined,
) {
  let seg = segCanvasRef.current;
  if (!seg || seg.width !== maskW || seg.height !== maskH) {
    seg = document.createElement('canvas');
    seg.width = maskW;
    seg.height = maskH;
    segCanvasRef.current = seg;
    segImageDataRef.current = new ImageData(maskW, maskH);
  }
  const segCtx = seg.getContext('2d');
  if (!segCtx) return;

  // Count pixels per region this frame.
  const counts = [0, 0, 0, 0, 0]; // index by LABEL
  for (let i = 0; i < labelMap.length; i++) counts[labelMap[i]]++;

  // Per-pixel watt intensity by region. Fallback to even distribution if
  // server hasn't sent watts (older builds) — still useful visually.
  const w = watts || {};
  const perPx: Record<number, number> = {
    [LABEL.HEAD]:  counts[LABEL.HEAD]  > 0 ? (w.head  ?? 1) / counts[LABEL.HEAD]  : 0,
    [LABEL.TORSO]: counts[LABEL.TORSO] > 0 ? (w.torso ?? 1) / counts[LABEL.TORSO] : 0,
    [LABEL.ARMS]:  counts[LABEL.ARMS]  > 0 ? (w.arms  ?? 1) / counts[LABEL.ARMS]  : 0,
    [LABEL.LEGS]:  counts[LABEL.LEGS]  > 0 ? (w.legs  ?? 1) / counts[LABEL.LEGS]  : 0,
  };
  const maxPerPx = Math.max(
    perPx[LABEL.HEAD], perPx[LABEL.TORSO], perPx[LABEL.ARMS], perPx[LABEL.LEGS], 1e-9,
  );

  // Pre-compute each region's colour. Cold → green/cyan, hot → red.
  // EmStyle: linear interpolation across HSL hue 130° (green) → 0° (red).
  const colorFor = (label: number): [number, number, number, number] => {
    const t = Math.max(0, Math.min(1, perPx[label] / maxPerPx));
    const hue = 130 * (1 - t); // 130=green, 65=yellow, 0=red
    const sat = 0.85;
    const light = 0.50 + 0.05 * t; // a touch brighter when hotter
    const [r, g, b] = hslToRgb(hue / 360, sat, light);
    return [r, g, b, 200];
  };
  const palette: Record<number, [number, number, number, number]> = {
    [LABEL.HEAD]:  colorFor(LABEL.HEAD),
    [LABEL.TORSO]: colorFor(LABEL.TORSO),
    [LABEL.ARMS]:  colorFor(LABEL.ARMS),
    [LABEL.LEGS]:  colorFor(LABEL.LEGS),
  };

  const imageData = segImageDataRef.current!;
  const rgba = imageData.data;
  for (let i = 0; i < labelMap.length; i++) {
    const label = labelMap[i];
    const color = palette[label];
    const j = i * 4;
    if (color) {
      rgba[j] = color[0];
      rgba[j + 1] = color[1];
      rgba[j + 2] = color[2];
      rgba[j + 3] = color[3];
    } else {
      rgba[j + 3] = 0;
    }
  }
  segCtx.putImageData(imageData, 0, 0);

  ctx.imageSmoothingEnabled = true;
  ctx.globalAlpha = 0.80;
  ctx.drawImage(seg, 0, 0, canvasW, canvasH);
  ctx.globalAlpha = 1;
}

/** Convert HSL [0,1] to 8-bit RGB. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255);
  };
  return [f(0), f(8), f(4)];
}
